import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { getPromoCodes, createPromoCode, togglePromoCode, deletePromoCode } from '../api';
import { toast } from 'sonner';
import { Plus, Trash2, ToggleLeft, ToggleRight, Tag } from 'lucide-react';

export const AdminPromoPage = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: '', type: 'percentage', value: '', max_uses: '', expires_at: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const res = await getPromoCodes();
      setCodes(res.data.promo_codes || []);
    } catch {
      toast.error('Erreur chargement codes promo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCodes(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code || !form.value) { toast.error('Code et valeur requis'); return; }
    setSaving(true);
    try {
      const payload = {
        code: form.code.toUpperCase().trim(),
        type: form.type,
        value: parseFloat(form.value),
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at || null,
      };
      await createPromoCode(payload);
      toast.success('Code promo créé');
      setForm({ code: '', type: 'percentage', value: '', max_uses: '', expires_at: '' });
      setShowForm(false);
      fetchCodes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await togglePromoCode(id);
      fetchCodes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Supprimer le code "${code}" ?`)) return;
    try {
      await deletePromoCode(id);
      toast.success('Code supprimé');
      fetchCodes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Codes promo</h1>
            <p className="text-muted-foreground text-sm">{codes.length} codes au total</p>
          </div>
          <button
            onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" /> Nouveau code
          </button>
        </div>

        {/* Formulaire de création */}
        {showForm && (
          <form onSubmit={handleCreate} className="bg-card border border-border rounded-xl p-6 space-y-4">
            <h2 className="font-semibold text-foreground">Créer un code promo</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Code *</label>
                <input
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground uppercase focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="EX: YETU20"
                  value={form.code}
                  onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Type *</label>
                <select
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                >
                  <option value="percentage">Pourcentage (%)</option>
                  <option value="fixed">Montant fixe ($)</option>
                  <option value="points">Points bonus</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Valeur *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Ex: 20"
                  value={form.value}
                  onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Utilisations max (vide = illimité)</label>
                <input
                  type="number"
                  min="1"
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Ex: 100"
                  value={form.max_uses}
                  onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Date d'expiration (vide = jamais)</label>
                <input
                  type="datetime-local"
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  value={form.expires_at}
                  onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 bg-muted rounded-lg text-sm hover:bg-muted/80 transition-colors">Annuler</button>
              <button type="submit" disabled={saving} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-colors disabled:opacity-50">
                {saving ? 'Création...' : 'Créer'}
              </button>
            </div>
          </form>
        )}

        {/* Liste des codes */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-muted-foreground">Chargement...</div>
          ) : codes.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <Tag className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p>Aucun code promo créé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Code</th>
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Type / Valeur</th>
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Utilisations</th>
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Expiration</th>
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Actif</th>
                    <th className="px-4 py-3 text-right text-muted-foreground font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {codes.map(c => (
                    <tr key={c.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3 font-mono font-bold text-primary">{c.code}</td>
                      <td className="px-4 py-3 text-foreground">
                        {c.type === 'percentage' ? `${c.value}%` : c.type === 'fixed' ? `$${c.value}` : `${c.value} pts`}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {c.uses_count ?? 0}{c.max_uses ? ` / ${c.max_uses}` : ' / ∞'}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">
                        {c.expires_at ? new Date(c.expires_at).toLocaleDateString('fr-FR') : 'Jamais'}
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => handleToggle(c.id)} className="transition-colors">
                          {c.is_active
                            ? <ToggleRight className="w-6 h-6 text-green-400" />
                            : <ToggleLeft className="w-6 h-6 text-muted-foreground" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => handleDelete(c.id, c.code)} className="p-1.5 rounded hover:bg-red-500/20 text-red-400 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};
